import { t } from "adapters/i18n";
import { LivestockWeightType } from "constants/livestock";
import arrayUtils from "shared/arrayUtils";
import { DATE_UNIT, DateUtils } from "shared/dateUtils";
import functionUtils from "shared/functionUtils";
import type { LivestockInfo } from "shared/types";
import type { DataResult, ServiceConstructor } from "../services.types";

type LotRelations = "goal" | "health-check" | "weight";

export type AccessoryWeightPayload =
  | {
      weightType: LivestockWeightType.Measured;
      operationDate?: string;
      weight?: number | null;
    }
  | {
      weightType: LivestockWeightType.Projected | LivestockWeightType.Goal;
      weight?: number | null;
    };

type UpdateAccessoriesPayload = Array<{
  livestockId: number;
  accessories: { goalId?: number | null; weights?: AccessoryWeightPayload[] };
}>;

export function LivestockServiceMixin<TBase extends ServiceConstructor>(
  Base: TBase
) {
  return class LivestockService extends Base {
    readonly livestockUrl = `${process.env.REACT_APP_ENDPOINT}herds/livestock`;

    async listLivestockFromPasturesIds(
      pastureIds: number[]
    ): Promise<DataResult<LivestockInfo>> {
      try {
        const pastureIdsChunked = arrayUtils.splitArrayInChunks(
          pastureIds,
          200
        );

        const data: LivestockInfo[] = [];

        await functionUtils.asyncForEach(
          pastureIdsChunked,
          async (pastureIdsSubset) => {
            const checkDate = DateUtils.now().add(1, DATE_UNIT.DAY).toDate();
            const query = {
              pasturesIds: pastureIdsSubset.join(","),
              checkDate,
            };
            const result = await this.get(
              `${this.livestockUrl}/from-pastures-ids`,
              query
            );
            const infoArray = result.data as LivestockInfo[];
            data.push(...infoArray);
          }
        );

        const total = data.length;

        return { data, total };
      } catch (_) {
        throw new Error(t("livestock:fetch-livestock-info-from-pasture-error"));
      }
    }

    async listLivestockFromFarm(
      farmId: number,
      options: { with?: LotRelations[] } = {}
    ): Promise<{ [pastureId: number]: LivestockInfo[] }> {
      try {
        const relationMap: Record<LotRelations, string> = {
          goal: "goal",
          "health-check": "healthCheck",
          weight: "weight",
        };

        const filter = this.montaURLParametros({
          include: options.with?.length
            ? options.with.map((relation) => relationMap[relation])
            : [],
        });

        const url = this.montaURL(
          `${this.livestockUrl}/from-farm/${farmId}`,
          filter
        );
        const result = await this.get(url);

        const data = (result.data?.data ?? null) as {
          farmId: number;
          pastures: Array<{ livestocks: LivestockInfo[]; pastureId: number }>;
        } | null;

        if (
          data === null ||
          data.farmId == null ||
          !Array.isArray(data.pastures)
        ) {
          return {};
        }

        const parsedResult = data.pastures.reduce((acc, pasture) => {
          const existing = acc.get(pasture.pastureId) ?? [];
          acc.set(pasture.pastureId, [...existing, ...pasture.livestocks]);
          return acc;
        }, new Map<number, LivestockInfo[]>());

        return Object.fromEntries(parsedResult);
      } catch (_) {
        throw new Error(t("livestock:fetch-livestock-from-farm-error"));
      }
    }

    async updateAccessories(payload: UpdateAccessoriesPayload): Promise<void> {
      try {
        const url = `${this.livestockUrl}/accessories`;
        await this.patch(url, payload);
      } catch (_) {
        throw new Error(t("livestock:update-lots-accessories-error"));
      }
    }
  };
}
